import { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { learningService } from "../../api/learningService";
import { getErrorMessage } from "../../utils/apiError";
import { formatManilaDateTime, formatManilaTime } from "../../utils/manilaTime";
import { LEARNING_KEY } from "./learningUtils";
import styles from "./SeminarCheckIn.module.css";

// Landing page of the seminar QR code: /me/learning/check-in?seminar=<id>&code=<code>.
// Checks in once on open (signed-out users come back here after login).
const SeminarCheckInView = () => {
  const [params] = useSearchParams();
  const seminarId = params.get("seminar");
  const code = params.get("code");
  const queryClient = useQueryClient();
  const started = useRef(false);

  const checkIn = useMutation({
    mutationFn: () => learningService.checkIn(seminarId, code),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [LEARNING_KEY] }),
  });

  // Once per page load (StrictMode runs effects twice in development)
  useEffect(() => {
    if (started.current || !seminarId || !code) return;
    started.current = true;
    checkIn.mutate();
  }, [seminarId, code, checkIn]);

  const res = checkIn.data;
  const seminar = res?.seminar;

  let body;
  if (!seminarId || !code) {
    body = (
      <div className={styles.state}>
        <i className={`bi bi-qr-code ${styles.iconMuted}`}></i>
        <h1 className={styles.title}>This check-in link is incomplete</h1>
        <p className={styles.text}>
          Scan the QR code shown at the seminar again.
        </p>
      </div>
    );
  } else if (checkIn.isError) {
    body = (
      <div className={styles.state}>
        <i className={`bi bi-x-circle-fill ${styles.iconError}`}></i>
        <h1 className={styles.title}>You couldn't be checked in</h1>
        <p className={styles.text}>
          {getErrorMessage(
            checkIn.error,
            "Something went wrong. Please try again.",
          )}
        </p>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => checkIn.mutate()}
          disabled={checkIn.isPending}
        >
          Try again
        </button>
      </div>
    );
  } else if (res) {
    body = (
      <div className={styles.state}>
        <i className={`bi bi-check-circle-fill ${styles.iconOk}`}></i>
        <h1 className={styles.title}>
          {res.alreadyCheckedIn
            ? "You're already checked in"
            : "You're checked in!"}
        </h1>
        {seminar && (
          <div className={styles.seminar}>
            <div className={styles.seminarTitle}>{seminar.title}</div>
            <div className={styles.text}>
              {formatManilaDateTime(seminar.startAt)}
              {seminar.endAt ? ` – ${formatManilaTime(seminar.endAt)}` : ""}
            </div>
            {seminar.venue && (
              <div className={styles.text}>
                <i className="bi bi-geo-alt me-1"></i>
                {seminar.venue}
              </div>
            )}
          </div>
        )}
        {res.checkedInAt && (
          <p className={styles.text}>
            Checked in at {formatManilaTime(res.checkedInAt)}
          </p>
        )}
      </div>
    );
  } else {
    body = (
      <div className={styles.state}>
        <span
          className="spinner-border text-primary"
          role="status"
          aria-label="Checking in"
        ></span>
        <p className={styles.text}>Checking you in…</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {body}
      <Link to="/me/learning" className={styles.back}>
        Go to My Learning
      </Link>
    </div>
  );
};

export default SeminarCheckInView;
